import { useCallback, useRef, useState, type ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";

export function NavigationConfirmationRouter({
  children,
}: {
  children: ReactNode;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const pendingConfirmation = useRef<((proceed: boolean) => void) | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  const requestConfirmation = useCallback(
    (nextMessage: string, callback: (proceed: boolean) => void) => {
      if (pendingConfirmation.current) {
        callback(false);
        return;
      }
      returnFocus.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      pendingConfirmation.current = callback;
      setMessage(nextMessage);
    },
    [],
  );

  const resolveConfirmation = (proceed: boolean) => {
    const callback = pendingConfirmation.current;
    pendingConfirmation.current = null;
    setMessage(null);
    callback?.(proceed);
  };

  return (
    <BrowserRouter getUserConfirmation={requestConfirmation}>
      {children}
      <AlertDialog
        open={message !== null}
        onOpenChange={(open) => {
          if (!open) resolveConfirmation(false);
        }}
      >
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (returnFocus.current?.isConnected)
              returnFocus.current.focus({ preventScroll: true });
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>{message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => resolveConfirmation(false)}>
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => resolveConfirmation(true)}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </BrowserRouter>
  );
}
